import React, { useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import LayoutContainer from 'components/Common/LayoutContainer/LayoutContainer'
import FormatCurrency from 'components/Common/FormatCurrency'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import GeoContext from 'contexts/geoContext'
import { convertAudToRegion } from 'luxPlus/lib/getRegionSaveAmountCopy'
import zIndex from 'styles/tools/z-index'
import LuxPlusMemberSavingsCarousel from './LuxPlusMemberSavingsCarousel'
import LuxPlusLandingCtaButtons from './LuxPlusLandingCtaButtons'

const SectionBackground = styled(Sheet)`
  margin-top: ${rem(24)};
  position: relative;
  z-index: ${zIndex.min};
`

const DesktopStackBreakpoint = styled(CSSBreakpoint)`
  flex: 1 0 0;
  min-width: 0;
`

const LeftColumn = styled(Group)`
  text-align: center;

  ${mediaQueryUp.desktop} {
    flex: 0 0 auto;
    width: ${rem(494)};
    padding: ${rem(64)} 0;
    text-align: left;
  }
`

function LuxPlusLandingMembershipSection() {
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)

  const oneMembershipText = intl.formatMessage({
    id: '1lHglWQRSTRiAXwO9_m1',
    defaultMessage: 'One membership.',
    description: 'First line of the membership section heading on LuxPlus page',
  })

  const enjoyDiscountsText = intl.formatMessage({
    id: 'rZubHR-WIdTu7MM63Xhv',
    defaultMessage: 'Enjoy discounts across hotels, tours & experiences worldwide',
    description: 'Membership section subtitle about available discounts across travel categories',
  })

  const savedByMembersText = intl.formatMessage({
    id: 'dGQOg8F0DZbhHC7Yu6gr',
    defaultMessage: 'saved by members in the past 30 days',
    description: 'Label for total amount saved by members in the past 30 days stat',
  })

  const offersWithDiscountsText = intl.formatMessage({
    id: 'YjUGo7BGphnaCtjqgmhN',
    defaultMessage: 'ways members have saved',
    description: 'Label for the number of ways members have saved statistic',
  })

  const becomeMemberText = intl.formatMessage({
    id: 'yt9djdbWPaN0RsMv9VBv',
    defaultMessage: 'Become a member',
    description: 'CTA button text to become a LuxPlus member on landing page',
  })

  const startFreePreviewText = intl.formatMessage({
    id: 'ltPA-u4kLSaiMXSTyTdp',
    defaultMessage: 'Start free preview',
    description: 'CTA button text to start a free LuxPlus preview on landing page',
  })

  const cancelAnytimeText = intl.formatMessage({
    id: 'u4LrLBJj283E87o74jhl',
    defaultMessage: 'Cancel anytime · No booking minimums',
    description: 'Disclaimer text about membership cancellation policy and no booking minimums',
  })

  return (
    <SectionBackground background="light-background" padding="24 0" tabletPadding="40 0">
      <LayoutContainer>
        <Group direction="vertical" desktopDirection="horizontal" gap={32} desktopGap={56} desktopVerticalAlign="center">
          <LeftColumn direction="vertical" gap={24} verticalAlign="center">
            <Group direction="vertical" gap={4}>
              <Heading variant="heading2">
                {oneMembershipText}<br />
                <FormattedMessage
                  id="uf7hiy2MUBQQfCjN6IqF"
                  defaultMessage="<em>Thousands</em> of ways to save."
                  description="Membership section heading second line emphasizing thousands of savings opportunities"
                  values={{
                    em: (chunks) => <em>{chunks}</em>,
                  }}
                />
              </Heading>
              <BodyText variant="medium">
                {enjoyDiscountsText}
              </BodyText>
            </Group>

            <Group direction="vertical" gap={8} horizontalAlign="center" desktopHorizontalAlign="start">
              <Group direction="horizontal" gap={8} verticalAlign="center">
                <Heading variant="heading4" colour="highlight-primary-dark"><FormatCurrency value={convertAudToRegion(2828747, currentRegionCode)} format="rounded" /></Heading>
                <Caption variant="medium" align="start">{savedByMembersText}</Caption>
              </Group>
              <Group direction="horizontal" gap={8} verticalAlign="center">
                <Heading variant="heading4" colour="highlight-primary-dark">
                  <FormatDecimal value={108000} />
                </Heading>
                <Caption variant="medium" align="start">{offersWithDiscountsText}</Caption>
              </Group>
            </Group>

            <CSSBreakpoint max="tablet">
              <LuxPlusMemberSavingsCarousel type="compact" />
            </CSSBreakpoint>

            <LuxPlusLandingCtaButtons
              becomeMemberLabel={becomeMemberText}
              startFreePreviewLabel={startFreePreviewText}
              fit="mobile-full-width"
              tabletHorizontalAlign="center"
              desktopHorizontalAlign="start"
              switchRegionLogoColour="inherit"
            />

            <BodyText variant="small" colour="normal-contrast">
              {cancelAnytimeText}
            </BodyText>
          </LeftColumn>

          <DesktopStackBreakpoint min="desktop">
            <LuxPlusMemberSavingsCarousel type="expanded" />
          </DesktopStackBreakpoint>
        </Group>
      </LayoutContainer>
    </SectionBackground>
  )
}

export default LuxPlusLandingMembershipSection
